// ---------------------------------------------------------------------------
// Muñeca's Couture — keyword product-name classifier
//
// Maps a (bilingual, messy) product NAME to one or more shop-category slugs.
// Pure + dependency-free; safe to import from server OR client code.
//
// Strategy:
//   1) Normalize the name (lowercase, strip accents/punct, collapse spaces).
//   2) Walk an ORDERED list of TYPE rules, most-specific first, and take the
//      first that matches -> the product's single "type" slug.
//   3) Independently test the FRAGRANCE rule (brand list + perfume words);
//      fragrances can co-exist with "other" if no garment type matched.
//   4) Independently test GENDER (mens/womens) — only when UNAMBIGUOUS.
//   5) If no type and no fragrance matched -> ["other"].
//
// Edit the keyword arrays below to tune. Order of TYPE_RULES matters.
// ---------------------------------------------------------------------------

export interface ShopCategory {
  slug: string;
  label: string;
  group: "type" | "gender" | "special";
}

// Display-ready, ordered. `label` is title-case English.
//
// NOTE: hoodies/outerwear/swimwear/footwear are intentionally left out of
// this display list — the current 110-item catalog has zero products that
// classify into any of them (verified against lib/catalog-index.json), so
// surfacing them as shop chips or nav links would be dead ends. The
// classifier keywords for all four still live in TYPE_RULES below and will
// classify real products the moment Wix carries them again; re-add the row
// here once a category has at least one product.
export const CATEGORIES: ShopCategory[] = [
  // --- product types ---
  { slug: "dresses", label: "Dresses", group: "type" },
  { slug: "jumpsuits", label: "Jumpsuits", group: "type" },
  { slug: "sets", label: "Sets", group: "type" },
  { slug: "tops", label: "Tops", group: "type" },
  { slug: "jeans", label: "Jeans", group: "type" },
  { slug: "pants", label: "Pants", group: "type" },
  { slug: "shorts", label: "Shorts", group: "type" },
  { slug: "skirts", label: "Skirts", group: "type" },
  { slug: "shapewear", label: "Shapewear", group: "type" },
  { slug: "accessories", label: "Accessories", group: "type" },
  // --- special ---
  { slug: "fragrances", label: "Fragrances", group: "special" },
  // --- gender (secondary, only when explicit) ---
  { slug: "mens", label: "Men's", group: "gender" },
  { slug: "womens", label: "Women's", group: "gender" },
];

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

/** Lowercase, strip accents, replace separators with spaces, collapse runs. */
function normalize(name: string): string {
  return (name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // remove accents (á->a, ñ->n)
    .replace(/[\/&._"'`]/g, " ") // separators -> space
    .replace(/[^a-z0-9\s-]/g, " ") // drop anything else
    .replace(/\s+/g, " ")
    .trim();
}

/** Whole-word-ish test: keyword surrounded by start/end or non-alnum. */
function has(text: string, kw: string): boolean {
  if (kw.includes(" ")) return text.includes(kw); // phrase: substring is fine
  // single token -> word boundary so "set" doesn't hit "sunset", "body" not "bodysuit-by-accident"
  const re = new RegExp(`(?:^|[^a-z0-9])${escapeRe(kw)}(?:$|[^a-z0-9])`);
  return re.test(text);
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function hasAny(text: string, kws: string[]): boolean {
  for (const kw of kws) if (has(text, kw)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Keyword tables  (English + Dominican Spanish + observed typos/abbrevs)
// ---------------------------------------------------------------------------

// SETS / two-piece — checked early because "Pants Set", "Skirt Set" etc.
// should be a SET, not pants/skirt. Requires an explicit set/conjunto cue.
const SET_KW = [
  "set", "sets", "conjunto", "2pc", "2 pc", "3pc", "3 pc", "2pcs",
  "two piece", "2 piece", "two-piece", "3 piece", "dos piezas",
];

// DRESSES
const DRESS_KW = [
  "dress", "dresses", "vestido", "vestidos", "maxi dress", "mini dress",
  "midi dress", "bodycon", "cocktail dress", "gown", "camison", "camisones",
  "vestir", // "de vestir" = dressy
];

// JUMPSUITS / rompers / one-piece
const JUMPSUIT_KW = [
  "jumpsuit", "jumpsuits", "romper", "rompers", "enterizo", "enterizos",
  "enteriso", "enterisos", "playsuit", "catsuit", "cat suit", "palazo",
  "palazzo", "overall", "overalls", "mono",
];

// SWIMWEAR (before tops/sets so "traje de bano" / "swim suit" route here)
const SWIM_KW = [
  "swim", "swimsuit", "swim suit", "swimwear", "bikini", "bikinis",
  "tankini", "traje de bano", "traje bano", "de bano", "bano", "monokini",
  "bathing suit", "beachwear", "cover up", "coverup",
];

// SHAPEWEAR / bodysuits / corsets / leggings / latex
const SHAPEWEAR_KW = [
  "faja", "fajas", "shapewear", "corset", "corser", "corsets", "bodysuit",
  "body suit", "bodysuits", "latex", "waist trainer", "shaper", "seamless body",
  "seemless body", "bodi",
];

// HOODIES (before outerwear/tops)
const HOODIE_KW = [
  "hoodie", "hoodies", "hoody", "hoodys", "capucha", "sudadera con capucha",
];

// OUTERWEAR — jackets, coats, vests, blazers, sweaters/sweatshirts
const OUTERWEAR_KW = [
  "jacket", "jackets", "jacke", "chaqueta", "chaquetas", "abrigo", "abrigos",
  "coat", "coats", "coach jacket", "windbreaker", "bomber", "puffer", "parka",
  "vest", "vests", "chaleco", "blazer", "blazers", "cardigan", "trench",
  "sweater", "sweaters", "suera", "sueras", "sweatshirt", "sweatshirts",
  "sudadera", "sudaderas", "crewneck", "crew neck", "pullover", "leder", "leather jacket",
];

// TOPS — shirts, tees, blouses, tanks, crop, etc.  (broad; checked after the
// more specific upper-body types above)
const TOP_KW = [
  "tee", "tees", "tshirt", "tshirts", "t shirt", "t shirts", "t-shirt",
  "shirt", "shirts", "camisa", "camisas", "camiseta", "camisetas",
  "blusa", "blusas", "blouse", "blouses", "bluza", "franela", "franelas",
  "poloche", "polo", "polos", "ticher", "tichers", "top", "tops",
  "crop top", "crop", "tank", "tanks", "halter", "tube top", "tube",
  "bralette", "bralett", "bra", "braa", "bustier", "long sleeve",
  "longsleeve", "long-sleeve", "short sleeve", "manga larga", "manga corta",
  "off shoulder", "off-shoulder", "one shoulder", "mesh top", "body",
  "knit top", "sueter",
];

// JEANS / denim
const JEANS_KW = [
  "jeans", "jean", "denim", "vaquero", "vaqueros", "kancan", "skinny jean",
  "mom jean", "boyfriend jean",
];

// PANTS — joggers, sweatpants, cargos, leggings, trousers
const PANTS_KW = [
  "pants", "pant", "pantalon", "pantalones", "joggers", "jogger", "jogguer",
  "sweatpants", "sweat pants", "sweatpant", "cargo", "cargos", "trouser",
  "trousers", "leggings", "legging", "leggin", "leguin", "leguins", "legins",
  "leguinss", "leggins", "lounge pants", "wide leg", "flare", "palazzo pants",
  "slacks", "chino", "chinos", "sweats", "sweat", "sudadera deportiva",
  "boxer", "boxers", "tights", "capri", "capris", "lycra",
];

// SHORTS
const SHORTS_KW = [
  "shorts", "short", "shor", "shors", "chor", "chors", // "shor"/"chor" = short typos
  "bermuda", "bermudas", "vermuda", "vermudas",
  "corto", "corta", "biker short", "short set",
];

// SKIRTS
const SKIRT_KW = [
  "skirt", "skirts", "falda", "faldas", "bubble skirt", "mini skirt",
  "midi skirt", "maxi skirt", "tutu",
];

// FOOTWEAR
const FOOTWEAR_KW = [
  "sneaker", "sneakers", "tenis", "zapato", "zapatos", "zapatilla",
  "zapatillas", "boot", "boots", "booter", "bota", "botas", "botin",
  "sandal", "sandals", "sandalia", "sandalias", "heel", "heels", "tacon",
  "tacones", "loafer", "loafers", "slides", "slide", "chancla", "chanclas",
  "mule", "mules", "slip-on", "slip on", "yeezy", "timbs", "timberland",
  "air force", "af1", "dunk", "footwear", "calzado", "platform shoe",
  "shoes", "shoe", "air jordan", "air max", "crocs", "moc", // "moc" = royal moc loafers
];

// Brand-only FALLBACK rules. These brands span types, so they are checked
// LAST (after every garment rule) and only catch the bare "<Brand> <code>"
// listings that no garment keyword matched. e.g. "Salvatore 284" -> footwear,
// "Salvatore Jeans 227" already matched jeans earlier and is unaffected.
const FOOTWEAR_BRAND_KW = ["salvatore", "jordan"]; // Salvatore shoes, Air Jordan/Jordan retros
const JEANS_BRAND_KW = ["waimea", "encore", "kancan"]; // denim brand lines

// ACCESSORIES — bags, jewelry, hats, belts, glasses, watches, small goods
const ACCESSORY_KW = [
  // bags
  "bag", "bags", "handbag", "handbags", "cartera", "carteras", "carterita",
  "bolso", "bolsos", "clutch", "clutches", "purse", "purses", "monedero",
  "wallet", "wallets", "backpack", "mochila", "tote", "crossbody", "pouch",
  // jewelry
  "chain", "chains", "cadena", "cadenas", "necklace", "collar", "collares",
  "arete", "aretes", "earring", "earrings", "earings", "earing", "argolla",
  "argollas", "argollita", "argollitas", "algolla", "anillo", "anillos",
  "ring", "rings", "pulsa", "pulsas", "pulsera", "pulseras", "pulcera",
  "pulceras", "bracelet", "bracelets", "brasalete", "brasaletes", "guillo",
  "guillos", "pendant", "charm", "cuban", "cubana", "rosario", "piercing",
  "broche", "crucifijo", "crusifijo", "cruz", "cross chain", "dije",
  // watches
  "watch", "watches", "relo", "reloj", "relojes", "relog",
  // headwear
  "hat", "hats", "gorra", "gorras", "gorro", "gorros", "cap", "caps",
  "snapback", "beanie", "sombrero", "visor", "bucket hat", "headband",
  "diadema", "scrunchie", "bandana", "durag", "du-rag", "trucker hat",
  // belts / eyewear / misc
  "belt", "belts", "correa", "correas", "cinturon", "sinturon", "cinto",
  "cinturilla", "faja correa", "sunglasses", "gafa", "gafas", "lente",
  "lentes", "glasses", "eyewear", "keychain", "llavero", "scarf", "bufanda",
  "guante", "guantes", "gloves", "medias", "media fishnet", "calcetin",
  "socks", "sock", "sox",
];

// FRAGRANCES — explicit perfume words OR a known designer/house brand name.
// Most names here lack a "perfume" word, so the brand list does the work.
const PERFUME_WORDS = [
  "perfume", "perfumes", "colonia", "cologne", "fragrance", "fragancia",
  "parfum", "parfume", "parfums", "eau de parfum", "eau de toilette",
  "edp", "edt", "edc", "pour homme", "pour femme", "body mist",
];

// Fragrance brand / line names. Kept SPECIFIC to avoid colliding with
// garment names (e.g. plain "phantom"/"legend"/"bleu" also appear as clothing,
// so we only list multi-word fragrance lines, never those bare words).
const FRAGRANCE_BRANDS = [
  // arab / niche houses common in the DR fragrance trade
  "lattafa", "orientica", "orienticaca", "armaf", "afnan", "rasasi",
  "al haramain", "ard al zaafaran", "maison alhambra", "khamrah",
  "amber rouge", "royal amber", "oud saffron", "oud", "saffron",
  "parfum de marly", "parfume de marly", "kilian", "xerjoff",
  "creed aventus", "aventus", "initio", "nishane", "amouage", "mancera",
  "montale", "asad", "yara", "amethyst",
  // Bond No.9 line (very common here)
  "bond no9", "bond no 9", "bond no.9", "bond no",
  // Jo Milano / Giorgio Milano fragrance lines (bare "milano" is a shoe code)
  "jo milano", "roll it", "zodiac",
  // Dubai-themed perfumes seen in catalog
  "dubai queen", "dubai king", "dubai garnet", "dubai rubi", "dubai platinum",
  // designer houses (specific lines only — never bare collide-y words)
  "invictus", "sauvage", "valentino uomo", "born in roma", "paco rabanne",
  "one million", "1million", "1 million", "givenchy pi", "gentleman givenchy",
  "eternity", "polo blue", "polo red", "azzaro", "rabanne",
  "carolina herrera", "good girl", "bad boy", "black opium", "la vie est belle",
  "bleu de chanel", "coco mademoiselle", "versace eros", "eros flame",
  "dylan blue", "bright crystal", "k by dolce", "jean paul gaultier",
  "le male", "stronger with you", "acqua di gio", "armani code", "my way",
  "ck one", "212 vip", "212 men", "boss bottled", "hugo boss the scent",
  "spicebomb", "scent of peace", "astor place", "greenwich village",
];

// ---------------------------------------------------------------------------
// Type rule table (ORDERED — most specific first; first match wins)
// ---------------------------------------------------------------------------

const TYPE_RULES: { slug: string; kws: string[] }[] = [
  { slug: "swimwear", kws: SWIM_KW }, // "traje de bano" before sets/tops
  { slug: "sets", kws: SET_KW }, // "Pants Set" -> set, not pants
  { slug: "jumpsuits", kws: JUMPSUIT_KW },
  { slug: "dresses", kws: DRESS_KW },
  { slug: "skirts", kws: SKIRT_KW },
  { slug: "shapewear", kws: SHAPEWEAR_KW }, // corset/faja/bodysuit/latex
  { slug: "hoodies", kws: HOODIE_KW },
  { slug: "outerwear", kws: OUTERWEAR_KW }, // jacket/sweater/vest/blazer
  { slug: "jeans", kws: JEANS_KW }, // before pants (denim trousers)
  { slug: "shorts", kws: SHORTS_KW }, // before pants ("corto")
  { slug: "pants", kws: PANTS_KW },
  { slug: "footwear", kws: FOOTWEAR_KW },
  { slug: "tops", kws: TOP_KW }, // broad upper-body catch
  { slug: "accessories", kws: ACCESSORY_KW },
  // brand-only fallbacks (checked only if nothing above matched)
  { slug: "jeans", kws: JEANS_BRAND_KW },
  { slug: "footwear", kws: FOOTWEAR_BRAND_KW },
];

// ---------------------------------------------------------------------------
// Gender (secondary) — assign ONLY when unambiguous.
// ---------------------------------------------------------------------------

const MENS_KW = [
  "men", "mens", "men s", "hombre", "hombres", "caballero", "caballeros",
  "uomo", "pour homme", "homme", "masculino", "para el", "for men", "man",
  "nino", "ninos", "boys",
];

const WOMENS_KW = [
  "women", "womens", "women s", "woman", "mujer", "mujeres", "dama", "damas",
  "femenino", "femenina", "pour femme", "femme", "for women", "ladies",
  "nina", "ninas", "girls",
];

// Garments that are inherently womenswear in this catalog.
const INHERENTLY_WOMENS = ["dresses", "skirts", "shapewear"];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Classify a product name into category slugs.
 * Returns: [typeSlug?] + ["fragrances"?] + [genderSlug?]
 * Falls back to ["other"] when nothing confident matches.
 */
export function categorize(name: string): string[] {
  const text = normalize(name);
  if (!text) return ["other"];

  const slugs: string[] = [];

  // 1) Fragrance (special) — independent of garment type.
  const isFragrance =
    hasAny(text, PERFUME_WORDS) || hasAny(text, FRAGRANCE_BRANDS);

  // 2) Type (first ordered match wins).
  let typeSlug: string | null = null;
  for (const rule of TYPE_RULES) {
    if (hasAny(text, rule.kws)) {
      typeSlug = rule.slug;
      break;
    }
  }

  if (typeSlug) slugs.push(typeSlug);
  if (isFragrance) slugs.push("fragrances");

  // 3) Gender (secondary) — only when explicit, or inherently-gendered type.
  const mens = hasAny(text, MENS_KW);
  const womens = hasAny(text, WOMENS_KW);
  if (mens && !womens) {
    slugs.push("mens");
  } else if (womens && !mens) {
    slugs.push("womens");
  } else if (!mens && !womens && typeSlug && INHERENTLY_WOMENS.includes(typeSlug)) {
    slugs.push("womens");
  }

  if (slugs.length === 0) return ["other"];
  return slugs;
}

/** True if `name` classifies into `slug`. */
export function inCategory(name: string, slug: string): boolean {
  return categorize(name).includes(slug);
}
